import { useCallback } from 'react';

import * as Sentry from '@sentry/react';

import { usePostApiV5UserJourneySteps } from '@liscio/api';

import {
  WHAT_TO_DO_TODAY_COMING_FROM,
  WHAT_TO_DO_TODAY_GUIDED_EXPERIENCE_PARAM,
  WHAT_TO_DO_TODAY_SOURCE,
} from './views/DashboardDesktopView/components/WhatToDoToday/constants';
import { buildJourneyStepPayload } from './views/DashboardDesktopView/components/WhatToDoToday/userJourneySteps';

export const GUIDED_FILE_STEP = 'upload_file';
export const GUIDED_DASHBOARD_RETURN_PATH = '/dashboard/home';
const GUIDED_FILE_RESUME_KEY = 'guided-file-intelligence-result';
const GUIDED_FILE_RESUME_TTL_MS = 30 * 60 * 1000;

export interface GuidedFileResumeState {
  document: {
    id: number;
    uuid?: string;
    originalName: string;
    suggestedName: string;
    displayName: string;
  };
  selection: {
    contactId: number;
    contactUuid: string;
    cpaUserId?: number;
    firstName: string;
    lastName: string;
    accountId: number;
    accountName: string;
  };
}

interface StoredGuidedFileResumeState extends GuidedFileResumeState {
  version: 1;
  expiresAt: number;
}

const positiveInteger = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value > 0;

const parseGuidedFileResume = (
  value: string | null
): StoredGuidedFileResumeState | null => {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as Partial<StoredGuidedFileResumeState>;
    const document = parsed.document;
    const selection = parsed.selection;
    if (
      parsed.version !== 1 ||
      typeof parsed.expiresAt !== 'number' ||
      parsed.expiresAt <= Date.now() ||
      !document ||
      !positiveInteger(document.id) ||
      typeof document.originalName !== 'string' ||
      typeof document.suggestedName !== 'string' ||
      typeof document.displayName !== 'string' ||
      (document.uuid != null && typeof document.uuid !== 'string') ||
      !selection ||
      !positiveInteger(selection.contactId) ||
      typeof selection.contactUuid !== 'string' ||
      (selection.cpaUserId != null &&
        !positiveInteger(selection.cpaUserId)) ||
      typeof selection.firstName !== 'string' ||
      typeof selection.lastName !== 'string' ||
      !positiveInteger(selection.accountId) ||
      typeof selection.accountName !== 'string'
    ) {
      return null;
    }
    return parsed as StoredGuidedFileResumeState;
  } catch {
    return null;
  }
};

export const saveGuidedFileResume = (state: GuidedFileResumeState) => {
  try {
    sessionStorage.setItem(
      GUIDED_FILE_RESUME_KEY,
      JSON.stringify({
        ...state,
        version: 1,
        expiresAt: Date.now() + GUIDED_FILE_RESUME_TTL_MS,
      } satisfies StoredGuidedFileResumeState)
    );
  } catch (error) {
    Sentry.captureException(error, {
      tags: { feature: 'guided_file_intelligence', stage: 'resume_store' },
    });
  }
};

export const consumeGuidedFileResume = (): GuidedFileResumeState | null => {
  try {
    const stored = parseGuidedFileResume(
      sessionStorage.getItem(GUIDED_FILE_RESUME_KEY)
    );
    sessionStorage.removeItem(GUIDED_FILE_RESUME_KEY);
    if (!stored) return null;
    return { document: stored.document, selection: stored.selection };
  } catch (error) {
    Sentry.captureException(error, {
      tags: { feature: 'guided_file_intelligence', stage: 'resume_read' },
    });
    return null;
  }
};

export type GuidedFileJourneyStage =
  | 'file_uploaded'
  | 'file_processed'
  | 'file_viewed'
  | 'file_send_selected'
  | 'file_sent_via_message';

export interface GuidedEventExtra {
  action?: 'send_message';
  destination?: 'file_detail' | 'message';
}

export const isGuidedFileDestination = (search: string) => {
  const params = new URLSearchParams(search);
  return (
    params.get('comingFrom') === WHAT_TO_DO_TODAY_COMING_FROM &&
    params.get(WHAT_TO_DO_TODAY_GUIDED_EXPERIENCE_PARAM) === 'true'
  );
};

export const guidedFilesPath = (documentId: number) =>
  `/files?doc=${documentId}&view=folder&comingFrom=${WHAT_TO_DO_TODAY_COMING_FROM}&${WHAT_TO_DO_TODAY_GUIDED_EXPERIENCE_PARAM}=true`;

export const guidedMessagePath = (
  documentId: number,
  accountId: number,
  contactId: number
) => {
  const params = new URLSearchParams({
    comingFrom: WHAT_TO_DO_TODAY_COMING_FROM,
    [WHAT_TO_DO_TODAY_GUIDED_EXPERIENCE_PARAM]: 'true',
    returnTo: GUIDED_DASHBOARD_RETURN_PATH,
    guidedDocumentId: String(documentId),
    guidedAccountId: String(accountId),
    guidedContactId: String(contactId),
  });

  return `/messages/new?${params.toString()}`;
};

export const guidedEventProperties = (
  documentId: number,
  extra: GuidedEventExtra = {}
) => ({
  ...extra,
  source: WHAT_TO_DO_TODAY_SOURCE,
  dashboard_step: GUIDED_FILE_STEP,
  document_id: documentId,
});

export const buildGuidedJourneyPayload = (
  stage: GuidedFileJourneyStage,
  documentId: number
) =>
  buildJourneyStepPayload({
    actionId: 'upload_file',
    stageKey: stage,
    metadata: {
      document_id: documentId,
    },
  });

export const useGuidedFileJourney = () => {
  const { mutateAsync } = usePostApiV5UserJourneySteps();

  return useCallback(
    (stage: GuidedFileJourneyStage, documentId: number) => {
      void mutateAsync({
        data: buildGuidedJourneyPayload(stage, documentId),
      }).catch((error) => {
        Sentry.captureException(error, {
          tags: { feature: 'guided_file_intelligence', stage },
          extra: { documentId },
        });
      });
    },
    [mutateAsync]
  );
};
